/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { accessAPI, formatFecha, TablaFiltrable } from "../../shared";

import { Accordion, AccordionTab } from "primereact/accordion";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";

const FILTROS_PLANIFICACIONES = [
  { campo: "_clienteText", label: "Cliente", labelPlural: "clientes" },
  { campo: "_certificadoTipoText", label: "Tipo de certificado", labelPlural: "tipos" },
  { campo: "_actividadText", label: "Actividad", labelPlural: "actividades" }
];
const SEARCH_FIELDS_PLANIFICACIONES = [
  "_clienteText",
  "_procesoText",
  "_certificadoTipoText",
  "_actividadText"
];

export default function PlanificacionesProximas({ toast }) {
  const [planificaciones, setPlanificaciones] = useState();
  const [calibraciones, setCalibraciones] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingCalibraciones, setLoadingCalibraciones] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/destacados/planificacionesproximas",
      null,
      (respuesta) => {
        setPlanificaciones(respuesta);
        setLoading(false);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar planificaciones",
          sticky: true
        });
        setLoading(false);
      }
    );
    accessAPI(
      "GET",
      "admin/destacados/calibracionesproximas",
      null,
      (respuesta) => {
        setCalibraciones(respuesta);
        setLoadingCalibraciones(false);
      },
      () => setLoadingCalibraciones(false)
    );
  }, [toast]);

  const sinPlanificaciones = !planificaciones || planificaciones.length === 0;
  const sinCalibraciones = !calibraciones || calibraciones.length === 0;

  if (
    !loading &&
    !loadingCalibraciones &&
    sinPlanificaciones &&
    sinCalibraciones
  ) {
    return null;
  }

  if (loading || loadingCalibraciones) {
    return (
      <div css={{ marginTop: "1em" }}>
        <Skeleton width="100%" height="3.5rem" borderRadius="6px" />
      </div>
    );
  }

  const datosPlanificaciones = (planificaciones || []).map((p) => {
    const cliente = p.proceso.cliente;
    const clienteText =
      cliente.nombrefantasia && cliente.nombrefantasia !== cliente.razonsocial
        ? `${cliente.razonsocial} (${cliente.nombrefantasia})`
        : cliente.razonsocial;
    return {
      ...p,
      _clienteText: clienteText,
      _procesoText: `${p.proceso.nombre} - ${p.proceso.nombrefantasia}`,
      _certificadoTipoText: p.proceso.certificadotipo?.nombre || "",
      _actividadText: p.actividadtipo.nombre
    };
  });

  const fechaBody = (planificacion) => {
    return formatFecha(planificacion.fecha);
  };

  const diasRestantesBody = (planificacion) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fecha = new Date(planificacion.fecha);
    fecha.setHours(0, 0, 0, 0);
    const dias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));

    if (dias < 0) {
      return <Tag value={`${Math.abs(dias)} atrás`} severity="danger" />;
    }
    if (dias === 0) {
      return <Tag value="Hoy" severity="danger" />;
    }
    if (dias <= 7) {
      return <Tag value={`${dias}`} severity="warning" />;
    }
    return <Tag value={`${dias}`} severity="info" />;
  };

  return (
    <Accordion
      activeIndex={null}
      css={{
        marginTop: "1em",
        "& .p-accordion-header-link": { color: "black" },
        "& .p-toggleable-content": { transition: "max-height 0.15s ease" }
      }}
    >
      <AccordionTab
        header={`Planificaciones (${(sinPlanificaciones ? 0 : planificaciones.length) + (sinCalibraciones ? 0 : calibraciones.length)})`}
      >
        {!sinPlanificaciones && (
          <>
            <div css={{ fontWeight: "bold", marginBottom: "0.5em" }}>
              Planificaciones
            </div>
            <TablaFiltrable
              value={datosPlanificaciones}
              filtros={FILTROS_PLANIFICACIONES}
              searchFields={SEARCH_FIELDS_PLANIFICACIONES}
              nombreArchivo="planificaciones_proximas"
              onRowClick={(e) => {
                const url = `/proceso/${e.data.proceso.id}/actividades?volver=/`;
                e.originalEvent.metaKey || e.originalEvent.ctrlKey
                  ? window.open(url, "_blank")
                  : navigate(url);
              }}
              emptyMessage="No hay planificaciones próximas"
              css={{
                "& .p-datatable-tbody > tr": {
                  cursor: "pointer"
                }
              }}
            >
              <Column header="Cliente" field="_clienteText" />
              <Column header="Proceso" field="_procesoText" />
              <Column header="Actividad" field="_actividadText" />
              <Column
                body={(planificacion) =>
                  planificacion.comentario ? (
                    <>
                      <Tooltip target={`.comentario-${planificacion.id}`} />
                      <i
                        className={`pi pi-info-circle comentario-${planificacion.id}`}
                        data-pr-tooltip={planificacion.comentario}
                        data-pr-position="top"
                        css={{
                          color: "var(--amarillo)",
                          fontSize: "1.2rem",
                          cursor: "pointer"
                        }}
                      />
                    </>
                  ) : null
                }
                style={{ width: "5em" }}
                bodyStyle={{ textAlign: "center" }}
              />
              <Column
                header="Fecha"
                body={fechaBody}
                style={{ width: "10em" }}
              />
              <Column
                header="Vencimiento"
                body={diasRestantesBody}
                style={{ width: "10em" }}
                alignHeader="center"
                bodyStyle={{ textAlign: "center" }}
              />
            </TablaFiltrable>
          </>
        )}
        {!sinCalibraciones && (
          <>
            <div
              css={{
                fontWeight: "bold",
                marginBottom: "0.5em",
                marginTop: sinPlanificaciones ? 0 : "1.5em"
              }}
            >
              Calibraciones
            </div>
            <TablaFiltrable
              value={calibraciones}
              searchFields={["nombre"]}
              nombreArchivo="calibraciones_proximas"
              onRowClick={(e) => {
                const url = `/instrumental/${e.data.id}`;
                e.originalEvent.metaKey || e.originalEvent.ctrlKey
                  ? window.open(url, "_blank")
                  : navigate(url);
              }}
              emptyMessage="No hay calibraciones próximas"
              css={{
                "& .p-datatable-tbody > tr": {
                  cursor: "pointer"
                }
              }}
            >
              <Column header="Instrumento" field="nombre" />
              <Column
                header="Fecha"
                body={(cal) => formatFecha(cal.proximaCalibracion)}
                style={{ width: "10em" }}
              />
              <Column
                header="Vencimiento"
                body={(cal) => {
                  const hoy = new Date();
                  hoy.setHours(0, 0, 0, 0);
                  const fecha = new Date(cal.proximaCalibracion);
                  fecha.setHours(0, 0, 0, 0);
                  const dias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));
                  if (dias < 0)
                    return (
                      <Tag
                        value={`${Math.abs(dias)} atrás`}
                        severity="danger"
                      />
                    );
                  if (dias === 0) return <Tag value="Hoy" severity="danger" />;
                  if (dias <= 7)
                    return <Tag value={`${dias}`} severity="warning" />;
                  return <Tag value={`${dias}`} severity="info" />;
                }}
                style={{ width: "10em" }}
                alignHeader="center"
                bodyStyle={{ textAlign: "center" }}
              />
            </TablaFiltrable>
          </>
        )}
      </AccordionTab>
    </Accordion>
  );
}
